/**
 * useContentReferences — reads content_bucket_reference rows for a bucket
 * (optionally scoped to a sub-category), with Drive thumbnails so the
 * Composer can render a picker grid.
 */
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface ContentReference {
  id: string;
  bucket_slug: string;
  sub_category: string | null;
  drive_file_id: string | null;
  drive_thumbnail_link: string | null;
  original_name: string | null;
  is_composition_anchor: boolean;
}

/** Style references (non-anchor). Filtered by sub_category when given. */
export function useContentReferences(bucketSlug: string | null, subCategory: string | null) {
  return useQuery<ContentReference[]>({
    queryKey: ['content-references', bucketSlug, subCategory],
    enabled: !!bucketSlug,
    queryFn: async () => {
      if (!bucketSlug) return [];
      let q = marketing
        .from('content_bucket_reference')
        .select('id,bucket_slug,sub_category,drive_file_id,drive_thumbnail_link,original_name,is_composition_anchor')
        .eq('bucket_slug', bucketSlug)
        .eq('is_composition_anchor', false)
        .order('added_at', { ascending: false });
      if (subCategory) q = q.eq('sub_category', subCategory);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ContentReference[];
    },
    staleTime: 30_000,
  });
}

/** Composition anchors only (from the `_Composition/` sub-folder). Not
 *  scoped to sub-category — anchors are cross-cutting arrangements. */
export function useCompositionAnchors(bucketSlug: string | null) {
  return useQuery<ContentReference[]>({
    queryKey: ['content-references-anchors', bucketSlug],
    enabled: !!bucketSlug,
    queryFn: async () => {
      if (!bucketSlug) return [];
      const { data, error } = await marketing
        .from('content_bucket_reference')
        .select('id,bucket_slug,sub_category,drive_file_id,drive_thumbnail_link,original_name,is_composition_anchor')
        .eq('bucket_slug', bucketSlug)
        .eq('is_composition_anchor', true)
        .order('added_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ContentReference[];
    },
    staleTime: 30_000,
  });
}
